import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Truck, ShieldCheck, Pencil, Lock } from "lucide-react";

export default function Staff() {
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/delivery-persons")).data });
  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/staff")).data });

  const [driverDialog, setDriverDialog] = useState(false);
  const [driverForm, setDriverForm] = useState({ name: "", phone: "", vehicle: "", area: "" });
  const [userDialog, setUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", phone: "", password: "", role: "staff" });

  const saveDriver = useMutation({
    mutationFn: () => api.post("/delivery-persons", driverForm),
    onSuccess: () => {
      toast.success("Delivery person added");
      qc.invalidateQueries({ queryKey: ["drivers"] });
      setDriverDialog(false);
      setDriverForm({ name: "", phone: "", vehicle: "", area: "" });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delDriver = useMutation({
    mutationFn: (id) => api.delete(`/delivery-persons/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["drivers"] }); },
  });

  const saveUser = useMutation({
    mutationFn: () => api.post("/staff", userForm),
    onSuccess: () => {
      toast.success("User account created");
      qc.invalidateQueries({ queryKey: ["staff"] });
      setUserDialog(false);
      setUserForm({ name: "", phone: "", password: "", role: "staff" });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const delUser = useMutation({
    mutationFn: (id) => api.delete(`/staff/${id}`),
    onSuccess: () => { toast.success("User removed"); qc.invalidateQueries({ queryKey: ["staff"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", role: "staff", password: "" });
  const openEditUser = (u) => { setEditUser(u); setEditForm({ name: u.name, phone: u.phone || "", role: u.role, password: "" }); };
  const updUser = useMutation({
    mutationFn: () => api.put(`/staff/${editUser.id}`, { name: editForm.name, phone: editForm.phone, role: editForm.role, password: editForm.password || null }),
    onSuccess: () => { toast.success("User updated"); qc.invalidateQueries({ queryKey: ["staff"] }); setEditUser(null); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Staff &amp; Team</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage delivery agents and app user accounts</p>
      </div>

      <Tabs defaultValue="drivers">
        <TabsList>
          <TabsTrigger value="drivers" data-testid="tab-drivers">Delivery Team</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">App Users</TabsTrigger>
        </TabsList>

        <TabsContent value="drivers" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setDriverDialog(true)} data-testid="add-driver-button"><Plus className="mr-2 h-4 w-4" /> Add Delivery Person</Button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {drivers.map((d, i) => (
              <motion.div key={d.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="p-5" data-testid={`driver-card-${d.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Truck className="h-5 w-5" /></span>
                      <div>
                        <div className="font-semibold">{d.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{d.phone}</div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => delDriver.mutate(d.id)} data-testid={`driver-delete-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline" className="font-mono">{d.vehicle}</Badge>
                    <Badge variant="outline">{d.area}</Badge>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3 text-center">
                    <div><div className="font-mono text-lg font-bold">{d.assigned}</div><div className="text-[10px] uppercase text-muted-foreground">Assigned</div></div>
                    <div><div className="font-mono text-lg font-bold text-emerald-600">{d.delivered}</div><div className="text-[10px] uppercase text-muted-foreground">Delivered</div></div>
                    <div><div className="font-mono text-sm font-bold">{formatINR(d.collected)}</div><div className="text-[10px] uppercase text-muted-foreground">Collected</div></div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setUserDialog(true)} data-testid="add-user-button"><Plus className="mr-2 h-4 w-4" /> Add User</Button>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone / Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((u) => (
                  <TableRow key={u.id} className="hover:bg-secondary/40" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono text-sm">{u.phone || u.email || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={u.role === "admin" ? "border-primary/30 bg-primary/10 text-primary" : ""}>
                          {u.role === "admin" ? <ShieldCheck className="mr-1 h-3 w-3" /> : <Truck className="mr-1 h-3 w-3" />}{u.role}
                        </Badge>
                        {u.is_super && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300" data-testid="super-admin-badge">Default admin</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {u.is_super ? (
                        <span title="Default admin — cannot be edited or deleted"><Lock className="ml-auto inline h-4 w-4 text-muted-foreground" data-testid="super-admin-lock" /></span>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditUser(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                          {u.id !== user?.id && (
                            <Button variant="ghost" size="icon" onClick={() => delUser.mutate(u.id)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={driverDialog} onOpenChange={setDriverDialog}>
        <DialogContent data-testid="driver-dialog">
          <DialogHeader>
            <DialogTitle>Add Delivery Person</DialogTitle>
            <DialogDescription>Register a driver with vehicle and route area.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={driverForm.name} onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })} data-testid="driver-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Phone</Label><Input value={driverForm.phone} onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })} data-testid="driver-phone-input" /></div>
              <div className="space-y-2"><Label>Vehicle No.</Label><Input value={driverForm.vehicle} onChange={(e) => setDriverForm({ ...driverForm, vehicle: e.target.value })} data-testid="driver-vehicle-input" /></div>
            </div>
            <div className="space-y-2"><Label>Area</Label><Input value={driverForm.area} onChange={(e) => setDriverForm({ ...driverForm, area: e.target.value })} data-testid="driver-area-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriverDialog(false)}>Cancel</Button>
            <Button onClick={() => saveDriver.mutate()} disabled={!driverForm.name} data-testid="driver-save-button">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userDialog} onOpenChange={setUserDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>Add App User</DialogTitle>
            <DialogDescription>Create a login account with name, phone and password — no email needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-2"><Label>Phone (used to log in)</Label><Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} data-testid="user-phone-input" /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (Delivery)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialog(false)}>Cancel</Button>
            <Button onClick={() => saveUser.mutate()} disabled={!userForm.name || !userForm.phone || !userForm.password} data-testid="user-save-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update name, phone, role or reset the password. Leave password blank to keep it unchanged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="user-edit-name" /></div>
            <div className="space-y-2"><Label>Phone (used to log in)</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="user-edit-phone" /></div>
            <div className="space-y-2"><Label>New Password (optional)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="user-edit-password" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (Delivery)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={() => updUser.mutate()} disabled={!editForm.name || updUser.isPending} data-testid="user-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
